import React from 'react';
import { Link } from 'react-router-dom';
import './PropertyManagers.css';
import BrandMark from './BrandMark';

/* ── Custom SVG icons (20×20, stroke, on-brand) ──── */
const IconTarget = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="10" cy="10" r="8.5" />
    <circle cx="10" cy="10" r="5" />
    <circle cx="10" cy="10" r="1.5" fill="currentColor" stroke="none" />
  </svg>
);

const IconWallet = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1.5" y="4.5" width="17" height="12" rx="2" />
    <path d="M1.5 8.5h17" />
    <circle cx="14.5" cy="12" r="1" fill="currentColor" stroke="none" />
  </svg>
);

const IconChart = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="16" height="16" rx="2" />
    <path d="M5.5 13.5V10.5" />
    <path d="M10 13.5V7.5" />
    <path d="M14.5 13.5V5.5" />
  </svg>
);

const IconTeam = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="7" cy="6.5" r="2.5" />
    <path d="M2.5 16c0-2.5 2-4.5 4.5-4.5s4.5 2 4.5 4.5" />
    <circle cx="14" cy="7.5" r="2" />
    <path d="M14 11.5c2 0 3.5 1.5 3.5 3.5" />
  </svg>
);

const IconMaple = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 2l1.5 3.5 3-1-1.5 3L16 9l-3.5 1 1 3.5-3.5-2-3.5 2 1-3.5L4 9l3-1.5L5.5 4.5l3 1L10 2z" />
    <path d="M10 14.5V18" />
  </svg>
);

const IconClipboard = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="3" width="12" height="15" rx="1.5" />
    <path d="M7 3V2a1 1 0 011-1h4a1 1 0 011 1v1" />
    <path d="M7.5 8.5h5" />
    <path d="M7.5 11.5h3" />
  </svg>
);

const IconDocument = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 2.5h7l4.5 4.5V17a1.5 1.5 0 01-1.5 1.5H5A1.5 1.5 0 013.5 17V4A1.5 1.5 0 015 2.5z" />
    <path d="M12 2.5v4.5h4.5" />
    <path d="M7 11h6" />
    <path d="M7 14h4" />
  </svg>
);

const IconShield = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 1.5l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9v-5l7-3z" />
    <path d="M7.5 10l2 2 3.5-4" />
  </svg>
);

function PropertyManagers() {
  return (
    <main className="App-main pm-page">
      {/* ── Hero ──────────────────────────────────────── */}
      <section className="pm-hero">
        <p className="pm-eyebrow">Property Managers</p>
        <h1>Manage every property in one place.</h1>
        <p className="pm-hero-subtitle">
          Screening, leasing, rent collection, and owner reporting.
          Built for property managers who want to grow without adding overhead.
        </p>
        <div className="pm-hero-actions">
          <Link to="/register" className="primary-button">Start free trial</Link>
          <Link to="/contact" className="secondary-button">Book a demo</Link>
        </div>
      </section>

      {/* ── How PMs use Rentual ────────────────────────── */}
      <section className="pm-section">
        <h2>How property managers use <BrandMark /></h2>
        <div className="pm-features-grid">
          <div className="card">
            <div className="pm-card-icon"><IconTarget /></div>
            <h3>Fill vacancies faster</h3>
            <p className="status-text">AI-powered screening, invite-only applications, and streamlined showings that cut vacancy days.</p>
          </div>
          <div className="card">
            <div className="pm-card-icon"><IconWallet /></div>
            <h3>Collect rent automatically</h3>
            <p className="status-text">Autopay enrollment, late fee automation, and payment tracking across your entire portfolio.</p>
          </div>
          <div className="card">
            <div className="pm-card-icon"><IconChart /></div>
            <h3>Keep owners informed</h3>
            <p className="status-text">Self-serve owner portal with monthly statements and real-time property updates.</p>
          </div>
          <div className="card">
            <div className="pm-card-icon"><IconTeam /></div>
            <h3>Scale your team</h3>
            <p className="status-text">Roles and permissions, shared inbox, and task management so your team stays aligned.</p>
          </div>
        </div>
      </section>

      {/* ── Owner Portal preview ──────────────────────── */}
      <section className="pm-section pm-portal-section">
        <h2>Your clients see exactly what they need. Nothing more, nothing less.</h2>
        <p className="pm-section-subtitle">
          Property owners get a clean, self-serve dashboard with the numbers that matter.
        </p>
        <div className="pm-portal-mockup">
          <div className="pm-portal-stats">
            <div className="pm-portal-stat">
              <h4>12</h4>
              <p>Properties</p>
            </div>
            <div className="pm-portal-stat">
              <h4>$18,400</h4>
              <p>Rent collected</p>
            </div>
            <div className="pm-portal-stat">
              <h4>2 open</h4>
              <p>Maintenance</p>
            </div>
          </div>
          <p className="pm-portal-label">Owner portal preview</p>
        </div>
      </section>

      {/* ── Pricing ───────────────────────────────────── */}
      <section className="pm-section">
        <h2>Simple, transparent pricing</h2>
        <p className="pm-section-subtitle">Pick the plan that matches your portfolio. All plans include screening, leasing, and rent collection.</p>
        <div className="pm-pricing-grid">
          <div className="pm-pricing-card">
            <h3>Starter</h3>
            <div className="pm-pricing-amount">$29<span>/mo</span></div>
            <p className="pm-pricing-units">Up to 10 units · 2 team members</p>
            <ul className="pm-pricing-features">
              <li>AI tenant screening</li>
              <li>Online rent collection</li>
              <li>Maintenance tracking</li>
              <li>Ontario standard lease generation</li>
              <li>Email support</li>
            </ul>
            <Link to="/register" className="primary-button">Get started</Link>
          </div>
          <div className="pm-pricing-card featured">
            <span className="pm-pricing-badge">Most popular</span>
            <h3>Growth</h3>
            <div className="pm-pricing-amount">$99<span>/mo</span></div>
            <p className="pm-pricing-units">Up to 50 units · 5 team members</p>
            <ul className="pm-pricing-features">
              <li>Everything in Starter</li>
              <li>Owner portal</li>
              <li>Automated owner statements</li>
              <li>Priority support</li>
              <li>Custom branding</li>
            </ul>
            <Link to="/register" className="primary-button">Get started</Link>
          </div>
          <div className="pm-pricing-card">
            <h3>Enterprise</h3>
            <div className="pm-pricing-amount">Custom</div>
            <p className="pm-pricing-units">Unlimited units · Unlimited team</p>
            <ul className="pm-pricing-features">
              <li>Everything in Growth</li>
              <li>Dedicated account manager</li>
              <li>API access</li>
              <li>Custom integrations</li>
              <li>SLA and onboarding support</li>
            </ul>
            <Link to="/contact" className="secondary-button">Contact sales</Link>
          </div>
        </div>
      </section>

      {/* ── Trust signals ─────────────────────────────── */}
      <section className="pm-section">
        <div className="pm-trust">
          <h2>Built for Canadian property managers</h2>
          <div className="pm-trust-grid">
            <div className="pm-trust-item">
              <span className="pm-trust-icon"><IconMaple /></span>
              <span>Canadian-built, PIPEDA compliant</span>
            </div>
            <div className="pm-trust-item">
              <span className="pm-trust-icon"><IconClipboard /></span>
              <span>TransUnion credit checks built in</span>
            </div>
            <div className="pm-trust-item">
              <span className="pm-trust-icon"><IconDocument /></span>
              <span>Automated Ontario standard lease generation</span>
            </div>
            <div className="pm-trust-item">
              <span className="pm-trust-icon"><IconShield /></span>
              <span>Bank-grade encryption + Stripe payments</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Bottom CTA ────────────────────────────────── */}
      <section className="pm-section pm-cta">
        <div className="pm-cta-card">
          <h2>Start managing properties today</h2>
          <p className="status-text">Sign up free and create your organization in minutes.</p>
          <Link to="/register" className="primary-button">Get started free</Link>
        </div>
      </section>
    </main>
  );
}

export default PropertyManagers;
